import { useMemo } from 'react';
import { useForm, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { IJobAssignmentData } from '@worker/types/job-assignment.type';
import { IRatingVisibility } from '@th-types/rating.type';

export const RATE_COMPANY_MAX_COMMENTS = 300;

type RateCompanyMode = 'CREATE' | 'PAYMENT_ONLY';

export function getRateCompanyMode(
  jobAssignment: IJobAssignmentData | undefined
): RateCompanyMode {
  return jobAssignment?.companyRating ? 'PAYMENT_ONLY' : 'CREATE';
}

const starField = z
  .number({ invalid_type_error: 'Please rate this category' })
  .int()
  .min(1, 'Please rate this category')
  .max(5);

const rateCompanyFormSchema = z.object({
  communication: starField,
  atEventManagement: starField,
  professionalism: starField,
  payment: z
    .number({ invalid_type_error: 'Please select payment timing' })
    .int()
    .nullable(),
  comments: z.string().max(RATE_COMPANY_MAX_COMMENTS),
  improvementComments: z.string().max(RATE_COMPANY_MAX_COMMENTS),
  visibility: z.boolean(),
});

export type RateCompanyFormValues = z.infer<typeof rateCompanyFormSchema>;

const EMPTY_DEFAULTS: DefaultValues<RateCompanyFormValues> = {
  communication: 0,
  atEventManagement: 0,
  professionalism: 0,
  payment: undefined,
  comments: '',
  improvementComments: '',
  visibility: false,
};

function buildDefaults(
  jobAssignment: IJobAssignmentData | undefined
): DefaultValues<RateCompanyFormValues> {
  if (!jobAssignment?.companyRating) return EMPTY_DEFAULTS;

  const rating = jobAssignment.companyRating;
  return {
    ...EMPTY_DEFAULTS,
    communication: rating.communication,
    atEventManagement: rating.atEventManagement,
    professionalism: rating.professionalism,
    comments: rating.comments ?? '',
    improvementComments: rating.improvementComments ?? '',
    visibility: rating.visibility === IRatingVisibility.PUBLIC,
  };
}

export function useRateCompanyForm(
  jobAssignment: IJobAssignmentData | undefined
) {
  const defaultValues = useMemo(
    () => buildDefaults(jobAssignment),
    [jobAssignment]
  );

  return useForm<RateCompanyFormValues>({
    resolver: zodResolver(rateCompanyFormSchema),
    defaultValues,
  });
}
